import { ComponentSize } from '@eleventheye/asui';
import { useAppSelector } from 'app/hooks';
import { getSelectedWeek, getSelectedYear } from 'app/slices/GamesSlice';
import TitledButtonGroup from 'components/button/group/components/TitledButtonGroup';
import { toNumber } from 'helpers/IdHelpers';
import useAllTeamStatsInfo from 'hooks/useAllTeamStatsInfo';
import useGetRankings from 'hooks/useGetRankings';
import React, { useCallback, useEffect, useState } from 'react';
import {
  FirstDownEfficiencySortBy,
  FourthDownDefenseEfficiencySortBy,
  FourthDownEfficiencySortBy,
  GameSubNavCategory,
  GameWeek,
  ITeamWeekRank,
  PassingDefenseSortBy,
  PassingSortBy,
  RedZoneDefenseEfficiencySortBy,
  RedZoneEfficiencySortBy,
  RushingSortBy,
  SackedTimesSortBy,
  ScoringAgainstBreakdownSortBy,
  ScoringBreakdownSortBy,
  ShowBy,
  ShowByTotalOrAverage,
  ThirdDownDefenseEfficiencySortBy,
  ThirdDownEfficiencySortBy,
  TotalsSortBy,
  YardsAllowedSortBy,
} from 'types';
import styles from './ShowByFilters.module.scss';

type ShowByFilterProps = {
  className?: string;
  showWeekSeasonFilter?: boolean;
  selectedSubCategory?: GameSubNavCategory;
  onSelectedRanking: (selectedRanking: ITeamWeekRank[]) => void;
  showSingleTeam?: boolean;
  showAllWeeks?: boolean;
  setDisplayValueDivisor?: (divisor: number) => void;
  showBy?: ShowBy;
  totalOrAverageShowBy?: ShowByTotalOrAverage;
};

const ShowByFilters: React.FC<ShowByFilterProps> = ({
  className = '',
  // showWeekSeasonFilter = false,
  selectedSubCategory,
  onSelectedRanking = () => undefined,
  showSingleTeam = false,
  showAllWeeks = false,
  setDisplayValueDivisor = () => undefined,
  showBy = 'weekTotals',
  totalOrAverageShowBy = 'total',
}) => {
  const selectedWeek = useAppSelector(getSelectedWeek);
  const selectedYear = useAppSelector(getSelectedYear);
  // const [showBy, setShowBy] = useState<ShowBy>('weekTotals');
  // const [totalOrAverageShowBy, setTotalOrAverageShowBy] = useState<ShowByTotalOrAverage>('total');
  const [passSortBy, setPassSortBy] = useState<PassingSortBy>('yards');
  const [rushSortBy, setRushSortBy] = useState<RushingSortBy>('yards');
  const [sortYardsAllowedBy, setSortYardsAllowedBy] = useState<YardsAllowedSortBy>('yards');
  const [sortTotalsBy, setSortTotalsBy] = useState<TotalsSortBy>('drives');
  const [sortScoringBreakdownBy, setSortScoringBreakdownBy] = useState<ScoringBreakdownSortBy>('pointsScored');
  const [sortScoringAgainstBreakdownBy, setSortScoringAgainstBreakdownBy] =
    useState<ScoringAgainstBreakdownSortBy>('pointsAllowed');
  const [sortFirstDownEfficiencyBy, setSortFirstDownEfficiencyBy] =
    useState<FirstDownEfficiencySortBy>('total 1st downs');
  const [sortThirdDownEfficiencyBy, setThirdDownEfficiencyBy] =
    useState<ThirdDownEfficiencySortBy>('3rd down attempts');
  const [sortFourthDownEfficiencyBy, setFourthDownEfficiencyBy] =
    useState<FourthDownEfficiencySortBy>('4th down attempts');
  const [sortRedZoneEfficiencyBy, setRedZoneEfficiencyBy] = useState<RedZoneEfficiencySortBy>('red zone attempts');
  const [sortSackedTimesBy, setSortSackedTimesBy] = useState<SackedTimesSortBy>('sackedTimes');
  const [sortPassingDefenseSubCatBy, setSortPassingDefenseSubCatBy] =
    useState<PassingDefenseSortBy>('passing attempts against');
  const [sortThirdDownDefenseEfficiencyBy, setSortThirdDownDefenseEfficiencyBy] =
    useState<ThirdDownDefenseEfficiencySortBy>('3rd down attempts against');
  const [sortFourthDownDefenseEfficiencyBy, setSortFourthDownDefenseEfficiencyBy] =
    useState<FourthDownDefenseEfficiencySortBy>('4th down attempts against');
  const [sortRedZoneDefenseEfficiencyBy, setSortRedZoneDefenseEfficiencyBy] =
    useState<RedZoneDefenseEfficiencySortBy>('red zone attempts against');

  const {
    initRankings,

    teamTotalYards, // 1
    teamTotalYardsPerPlay, // 2
    teamTotalPassingYards, // 3
    teamTotalRushingYards, // 4
    teamTotalPassAttempts, // 5
    teamTotalPassCompletions, // 6
    teamTotalPassingPercentage, // 7
    teamTotalPassingYardsPerAttempt, // 8
    teamTotalRushingAttempts, // 9
    teamTotalRushingYardsPerAttempt, // 10
    teamTotalPointsScored, // 11
    teamTotalInterceptionsThrown, // 12
    teamTotalPlays, // 13
    teamTotalFumblesLost, // 14

    teamTotalTimeOfPossession, // 15
    teamTotalPenalties, // 16
    teamTotalPenaltyYards, // 17

    teamTotalYardsAllowed, // 18
    teamTotalPassingYardsAllowed, // 19
    teamTotalRushingYardsAllowed, // 20
    teamTotalPointsAllowed, // 21
    teamTotalSacks, // 22
    teamTotalDefensiveInterceptions, // 23
    teamTotalForcedFumbles, // 24
    teamTotalSafeties, // 25
    teamTotalDefensiveOrSpecialTeamsTds, // 26

    teamTotalScoringPassingTDAgainst, // 27
    teamTotalScoringRushingTDAgainst, // 28
    teamTotalScoringFGAgainst, // 29
    teamTotalScoringEPAgainst, // 30
    teamTotalScoring2PtAgainst, // 31
    teamTotalScoringPassingTDFor, // 32
    teamTotalScoringRushingTDFor, // 33
    teamTotalScoringFGFor, // 34
    teamTotalScoringEPFor, // 35
    teamTotalScoring2PtFor, // 36
    teamTotalScoringTotalTDFor, // 37
    teamTotalScoringTotalTDAgainst, // 38

    teamTotalSackedTimes, // 39
    teamTotalSackedYardsLost, // 40
    teamTotalDrives, // 41

    teamTotalThirdDownAttempts, // 42
    teamTotalThirdDownConverted, // 43
    teamTotalRedZoneScored, // 44
    teamTotalRedZoneAttempts, // 45
    teamTotalFirstDowns, // 46
    teamTotalFirstDownsFromPenalties, // 47
    teamTotalFirstDownsFromRushing, // 48
    teamTotalFirstDownsFromPassing, // 49
    teamTotalFourthDownAttempts, // 50
    teamTotalFourthDownScored, // 51

    teamTotalThirdDownEfficiency, // 52
    teamTotalFourthDownEfficiency, // 53
    teamTotalRedZoneEfficiency, // 54

    teamTotalPassIncompletions, // 55
    teamTotalPassAttemptsAgainst, // 56
    teamTotalPassIncompletePercentage, // 57
    teamTotalThirdDownAttemptsAgainst, // 58
    teamTotalThirdDownStops, // 59
    teamTotalThirdDownStopEfficiency, // 60
    teamTotalFourthDownStops, // 61
    teamTotalFourthDownAttemptsAgainst, // 62
    teamTotalFourthDownStopEfficiency, // 63
    teamTotalRedZoneStops, // 64
    teamTotalRedZoneAttemptsAgainst, // 65
    teamTotalRedZoneStopEfficiency, // 66
  } = useGetRankings();

  const maxWeek = showAllWeeks ? GameWeek.Week18 : selectedWeek;
  const { initOneTeamRankings, initAllTeamsRankings } = useAllTeamStatsInfo(maxWeek, selectedYear, showBy);

  useEffect(() => {
    if (!initRankings || !initOneTeamRankings || !initAllTeamsRankings) return;

    if (showSingleTeam) {
      initOneTeamRankings(initRankings);
    } else {
      initAllTeamsRankings(initRankings);
    }
  }, [showSingleTeam, initAllTeamsRankings, initRankings, initOneTeamRankings]);

  const getChartDataByPassingCategory = useCallback(
    (subCategory: PassingSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'completions':
          return teamTotalPassCompletions;
        case 'attempts':
          return teamTotalPassAttempts;
        case 'percentage':
          return teamTotalPassingPercentage;
        case 'yards':
          return teamTotalPassingYards;
        case 'yardsPerPass':
          return teamTotalPassingYardsPerAttempt;
        default:
          return [];
      }
    },
    [
      teamTotalPassCompletions,
      teamTotalPassAttempts,
      teamTotalPassingPercentage,
      teamTotalPassingYards,
      teamTotalPassingYardsPerAttempt,
    ]
  );

  const getChartDataByTotalsCategory = useCallback(
    (subCategory: TotalsSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'drives':
          return teamTotalDrives;
        case 'yards':
          return teamTotalYards;
        case 'plays':
          return teamTotalPlays;
        case 'yardsPerPlay':
          return teamTotalYardsPerPlay;
        case 'totalTimeOfPossession':
          return teamTotalTimeOfPossession;
        default:
          return [];
      }
    },
    [teamTotalDrives, teamTotalYards, teamTotalPlays, teamTotalYardsPerPlay, teamTotalTimeOfPossession]
  );

  const getChartDataByRushingCategory = useCallback(
    (subCategory: RushingSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'yards':
          return teamTotalRushingYards;
        case 'attempts':
          return teamTotalRushingAttempts;
        case 'yardsPerRush':
          return teamTotalRushingYardsPerAttempt;
        default:
          return [];
      }
    },
    [teamTotalRushingYards, teamTotalRushingAttempts, teamTotalRushingYardsPerAttempt]
  );

  const getChartDataByYardsAllowedCategory = useCallback(
    (subCategory: YardsAllowedSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'pass':
          return teamTotalPassingYardsAllowed;
        case 'rush':
          return teamTotalRushingYardsAllowed;
        case 'yards':
        default:
          return teamTotalYardsAllowed;
      }
    },
    [teamTotalYardsAllowed, teamTotalPassingYardsAllowed, teamTotalRushingYardsAllowed]
  );

  const getChartDataByScoringBreakdownCategory = useCallback(
    (subCategory: ScoringBreakdownSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'pointsScored':
          return teamTotalPointsScored;
        case 'totalTDs':
          return teamTotalScoringTotalTDFor;
        case 'passTds':
          return teamTotalScoringPassingTDFor;
        case 'rushTds':
          return teamTotalScoringRushingTDFor;
        case 'fgs':
          return teamTotalScoringFGFor;
        case 'extraPoint':
          return teamTotalScoringEPFor;
        case 'twoPtConversions':
          return teamTotalScoring2PtFor;
        case 'safeties':
          return teamTotalSafeties;
        case 'defensiveOrSpecialTeamsTds':
          return teamTotalDefensiveOrSpecialTeamsTds;
      }
    },
    [
      teamTotalPointsScored,
      teamTotalScoringTotalTDFor,
      teamTotalScoringPassingTDFor,
      teamTotalScoringRushingTDFor,
      teamTotalScoringFGFor,
      teamTotalScoringEPFor,
      teamTotalScoring2PtFor,
      teamTotalSafeties,
      teamTotalDefensiveOrSpecialTeamsTds,
    ]
  );

  const getChartDataByAllowedScoringBreakdownCategory = useCallback(
    (subCategory: ScoringAgainstBreakdownSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'pointsAllowed':
          return teamTotalPointsAllowed;
        case 'totalTDs':
          return teamTotalScoringTotalTDAgainst;
        case 'passTds':
          return teamTotalScoringPassingTDAgainst;
        case 'rushTds':
          return teamTotalScoringRushingTDAgainst;
        case 'fgs':
          return teamTotalScoringFGAgainst;
        case 'extraPoint':
          return teamTotalScoringEPAgainst;
        case 'twoPtConversions':
          return teamTotalScoring2PtAgainst;
      }
    },
    [
      teamTotalPointsAllowed,
      teamTotalScoringTotalTDAgainst,
      teamTotalScoringPassingTDAgainst,
      teamTotalScoringRushingTDAgainst,
      teamTotalScoringFGAgainst,
      teamTotalScoringEPAgainst,
      teamTotalScoring2PtAgainst,
    ]
  );

  const getChartDataByFirstDownEfficiencyCategory = useCallback(
    (subCategory: FirstDownEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'total 1st downs':
          return teamTotalFirstDowns;
        case '1st downs from penalties':
          return teamTotalFirstDownsFromPenalties;
        case '1st downs by pass':
          return teamTotalFirstDownsFromPassing;
        case '1st downs by rush':
          return teamTotalFirstDownsFromRushing;
      }
    },
    [
      teamTotalFirstDowns,
      teamTotalFirstDownsFromPenalties,
      teamTotalFirstDownsFromPassing,
      teamTotalFirstDownsFromRushing,
    ]
  );

  const getChartDataByThirdDownEfficiencyCategory = useCallback(
    (subCategory: ThirdDownEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case '3rd down attempts':
          return teamTotalThirdDownAttempts;
        case '3rd down converted':
          return teamTotalThirdDownConverted;
        case '3rd down efficiency':
          return teamTotalThirdDownEfficiency;
      }
    },
    [teamTotalThirdDownAttempts, teamTotalThirdDownConverted, teamTotalThirdDownEfficiency]
  );

  const getChartDataByFourthDownEfficiencyCategory = useCallback(
    (subCategory: FourthDownEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case '4th down attempts':
          return teamTotalFourthDownAttempts;
        case '4th down converted':
          return teamTotalFourthDownScored;
        case '4th down efficiency':
          return teamTotalFourthDownEfficiency;
      }
    },
    [teamTotalFourthDownAttempts, teamTotalFourthDownScored, teamTotalFourthDownEfficiency]
  );

  const getChartDataByRedZoneEfficiencyCategory = useCallback(
    (subCategory: RedZoneEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'red zone attempts':
          return teamTotalRedZoneAttempts;
        case 'red zone scored':
          return teamTotalRedZoneScored;
        case 'red zone efficiency':
          return teamTotalRedZoneEfficiency;
      }
    },
    [teamTotalRedZoneAttempts, teamTotalRedZoneScored, teamTotalRedZoneEfficiency]
  );

  const getChartDataByThirdDownStopEfficiencyCategory = useCallback(
    (subCategory: ThirdDownDefenseEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case '3rd down attempts against':
          return teamTotalThirdDownAttemptsAgainst;
        case '3rd down stops':
          return teamTotalThirdDownStops;
        case '3rd down stop efficiency':
          return teamTotalThirdDownStopEfficiency;
      }
    },
    [teamTotalThirdDownAttemptsAgainst, teamTotalThirdDownStops, teamTotalThirdDownStopEfficiency]
  );

  const getChartDataByFourthDownStopEfficiencyCategory = useCallback(
    (subCategory: FourthDownDefenseEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case '4th down attempts against':
          return teamTotalFourthDownAttemptsAgainst;
        case '4th down stops':
          return teamTotalFourthDownStops;
        case '4th down stop efficiency':
          return teamTotalFourthDownStopEfficiency;
      }
    },
    [teamTotalFourthDownAttemptsAgainst, teamTotalFourthDownStops, teamTotalFourthDownStopEfficiency]
  );

  const getChartDataByRedZoneStopEfficiencyCategory = useCallback(
    (subCategory: RedZoneDefenseEfficiencySortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'red zone attempts against':
          return teamTotalRedZoneAttemptsAgainst;
        case 'red zone stops':
          return teamTotalRedZoneStops;
        case 'red zone stop efficiency':
          return teamTotalRedZoneStopEfficiency;
      }
    },
    [teamTotalRedZoneAttemptsAgainst, teamTotalRedZoneStops, teamTotalRedZoneStopEfficiency]
  );

  const getChartDataByPassingDefenseCategory = useCallback(
    (subCategory: PassingDefenseSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'passing attempts against':
          return teamTotalPassAttemptsAgainst;
        case 'pass incompletions':
          return teamTotalPassIncompletions;
        case 'pass incompletion percentage':
          return teamTotalPassIncompletePercentage;
      }
    },
    [teamTotalPassAttemptsAgainst, teamTotalPassIncompletions, teamTotalPassIncompletePercentage]
  );

  const getChartDataBySackedTimesCategory = useCallback(
    (subCategory: SackedTimesSortBy): ITeamWeekRank[] => {
      switch (subCategory) {
        case 'sackedTimes':
          return teamTotalSackedTimes;
        case 'sackedYardsLost':
          return teamTotalSackedYardsLost;
        default:
          return [];
      }
    },
    [teamTotalSackedTimes, teamTotalSackedYardsLost]
  );

  const getGraphDataByCategory = useCallback(
    (
      selectedCat: GameSubNavCategory,
      selectedSubCat: PassingSortBy,
      selectedRushingSubCat: RushingSortBy,
      selectedYardsAllowedSubCat: YardsAllowedSortBy,
      selectedTotalsSubCat: TotalsSortBy,
      selectScoringBreakdownBy: ScoringBreakdownSortBy,
      sortScoringAgainstBreakdownSubCat: ScoringAgainstBreakdownSortBy,
      sortFirstDownEfficiencySubCat: FirstDownEfficiencySortBy,
      sortThirdDownEfficiencySubCat: ThirdDownEfficiencySortBy,
      sortFourthDownEfficiencySubCat: FourthDownEfficiencySortBy,
      sortRedZoneEfficiencySubCat: RedZoneEfficiencySortBy,
      sortPassingDefenseSubCatBy: PassingDefenseSortBy,
      sortThirdDownDefenseEfficiencyBy: ThirdDownDefenseEfficiencySortBy,
      sortFourthDownDefenseEfficiencyBy: FourthDownDefenseEfficiencySortBy,
      sortRedZoneDefenseEfficiencyBy: RedZoneDefenseEfficiencySortBy,
      sortSackedTimesSubCat: SackedTimesSortBy
    ): ITeamWeekRank[] => {
      switch (selectedCat) {
        case 'ScoringBreakdown':
          return getChartDataByScoringBreakdownCategory(selectScoringBreakdownBy);
        case 'ScoringAgainstBreakdown':
          return getChartDataByAllowedScoringBreakdownCategory(sortScoringAgainstBreakdownSubCat);
        case 'Total':
          return getChartDataByTotalsCategory(selectedTotalsSubCat);
        case 'InterceptionsThrown':
          return teamTotalInterceptionsThrown;
        case 'FumblesLost':
          return teamTotalFumblesLost;
        case 'Penalties':
          return teamTotalPenalties;
        case 'PenaltyYards':
          return teamTotalPenaltyYards;
        // case 'TimeOfPossession':
        //   return teamTotalTimeOfPossession;
        case 'Passing':
          return getChartDataByPassingCategory(selectedSubCat);
        case 'Rushing':
          return getChartDataByRushingCategory(selectedRushingSubCat);
        case 'YardsAllowed':
          return getChartDataByYardsAllowedCategory(selectedYardsAllowedSubCat);
        case 'FirstDownEfficiency':
          return getChartDataByFirstDownEfficiencyCategory(sortFirstDownEfficiencySubCat);
        case 'ThirdDownEfficiency':
          return getChartDataByThirdDownEfficiencyCategory(sortThirdDownEfficiencySubCat);
        case 'FourthDownEfficiency':
          return getChartDataByFourthDownEfficiencyCategory(sortFourthDownEfficiencySubCat);
        case 'RedZoneEfficiency':
          return getChartDataByRedZoneEfficiencyCategory(sortRedZoneEfficiencySubCat);
        case 'PassIncompletePercentage':
          return getChartDataByPassingDefenseCategory(sortPassingDefenseSubCatBy);
        case 'ThirdDownStopEfficiency':
          return getChartDataByThirdDownStopEfficiencyCategory(sortThirdDownDefenseEfficiencyBy);
        case 'FourthDownStopEfficiency':
          return getChartDataByFourthDownStopEfficiencyCategory(sortFourthDownDefenseEfficiencyBy);
        case 'RedZoneStopEfficiency':
          return getChartDataByRedZoneStopEfficiencyCategory(sortRedZoneDefenseEfficiencyBy);
        case 'SackedTimes':
          return getChartDataBySackedTimesCategory(sortSackedTimesSubCat);
        case 'Sacks':
          return teamTotalSacks;
        case 'Interceptions':
          return teamTotalDefensiveInterceptions;
        case 'FumblesRecovered':
          return teamTotalForcedFumbles;
        case 'Safeties':
          return teamTotalSafeties;
        case 'DefensiveTouchdowns':
          return teamTotalDefensiveOrSpecialTeamsTds;
        default:
          return [];
      }
    },
    [
      getChartDataByScoringBreakdownCategory,
      getChartDataByAllowedScoringBreakdownCategory,
      getChartDataByTotalsCategory,
      getChartDataByPassingCategory,
      getChartDataByRushingCategory,
      getChartDataByYardsAllowedCategory,
      getChartDataByFirstDownEfficiencyCategory,
      getChartDataByThirdDownEfficiencyCategory,
      getChartDataByFourthDownEfficiencyCategory,
      getChartDataByRedZoneEfficiencyCategory,
      getChartDataByPassingDefenseCategory,
      getChartDataByThirdDownStopEfficiencyCategory,
      getChartDataByFourthDownStopEfficiencyCategory,
      getChartDataByRedZoneStopEfficiencyCategory,
      getChartDataBySackedTimesCategory,
      teamTotalInterceptionsThrown,
      teamTotalFumblesLost,
      teamTotalPenalties,
      teamTotalPenaltyYards,
      // teamTotalTimeOfPossession,
      teamTotalSacks,
      teamTotalDefensiveInterceptions,
      teamTotalForcedFumbles,
      teamTotalSafeties,
      teamTotalDefensiveOrSpecialTeamsTds,
    ]
  );

  useEffect(() => {
    if (selectedSubCategory) {
      const ranking = getGraphDataByCategory(
        selectedSubCategory,
        passSortBy,
        rushSortBy,
        sortYardsAllowedBy,
        sortTotalsBy,
        sortScoringBreakdownBy,
        sortScoringAgainstBreakdownBy,
        sortFirstDownEfficiencyBy,
        sortThirdDownEfficiencyBy,
        sortFourthDownEfficiencyBy,
        sortRedZoneEfficiencyBy,
        sortPassingDefenseSubCatBy,
        sortThirdDownDefenseEfficiencyBy,
        sortFourthDownDefenseEfficiencyBy,
        sortRedZoneDefenseEfficiencyBy,
        sortSackedTimesBy
      );

      onSelectedRanking(ranking);
    }
  }, [
    getGraphDataByCategory,
    onSelectedRanking,
    passSortBy,
    rushSortBy,
    selectedSubCategory,
    sortScoringAgainstBreakdownBy,
    sortScoringBreakdownBy,
    sortTotalsBy,
    sortYardsAllowedBy,
    sortFirstDownEfficiencyBy,
    sortThirdDownEfficiencyBy,
    sortFourthDownEfficiencyBy,
    sortRedZoneEfficiencyBy,
    sortPassingDefenseSubCatBy,
    sortThirdDownDefenseEfficiencyBy,
    sortFourthDownDefenseEfficiencyBy,
    sortRedZoneDefenseEfficiencyBy,
    sortSackedTimesBy,
  ]);

  useEffect(() => {
    if (totalOrAverageShowBy === 'average') {
      const numberOfWeeks = toNumber(selectedWeek);
      const divisor = numberOfWeeks;
      console.log('setDisplayValueDivisor', numberOfWeeks, divisor);
      // const divisor = numberOfWeeks >= 14 ? numberOfWeeks - 1 : numberOfWeeks;
      setDisplayValueDivisor(divisor);
    } else {
      setDisplayValueDivisor(1);
    }
  }, [setDisplayValueDivisor, selectedWeek, totalOrAverageShowBy]);

  const convertLabelCodeText = (text: string): string => {
    if (text === 'yards per pass') return 'yardsPerPass';
    if (text === 'yards per carry') return 'yardsPerRush';
    if (text === 'yards per play') return 'yardsPerPlay';
    if (text === 'yardsPerRush') return 'yards per carry';
    if (text === 'yardsPerPass') return 'yards per pass';
    if (text === 'yardsPerPlay') return 'yards per play';
    if (text === 'pass td') return 'passTds';
    if (text === 'passTds') return 'pass td';
    if (text === 'rush td') return 'rushTds';
    if (text === 'rushTds') return 'rush td';
    if (text === 'fg') return 'fgs';
    if (text === 'fgs') return 'fg';
    if (text === 'pat') return 'extraPoint';
    if (text === 'extraPoint') return 'pat';
    if (text === '2pt-pat') return 'twoPtConversions';
    if (text === 'twoPtConversions') return '2pt-pat';
    if (text === 'dst td') return 'defensiveOrSpecialTeamsTds';
    if (text === 'defensiveOrSpecialTeamsTds') return 'dst td';
    if (text === 'pointsScored') return 'points scored';
    if (text === 'points scored') return 'pointsScored';
    if (text === 'pointsAllowed') return 'points allowed';
    if (text === 'points allowed') return 'pointsAllowed';
    if (text === 'totalTDs') return 'total tds';
    if (text === 'total tds') return 'totalTDs';
    if (text === 'times sacked') return 'sackedTimes';
    if (text === 'sackedTimes') return 'times sacked';
    if (text === 'sackedYardsLost') return 'yards lost';
    if (text === 'yards lost') return 'sackedYardsLost';
    if (text === 'totalTimeOfPossession') return 'time of possession';
    if (text === 'time of possession') return 'totalTimeOfPossession';

    return text;
  };

  const convertYardsAllowedLabel = (text: string): string => {
    if (text === 'yards') return 'total';
    if (text === 'total') return 'yards';

    return text;
  };

  return (
    <div className={`${styles.ShowByFilters} ${className}`}>
      {/* {showWeekSeasonFilter && (
        <div className={styles.TeamStatsFiltersColumn}>
          <TitledButtonGroup
            title="Show by"
            useUpperCase
            buttonItems={['week', 'season']}
            onItemSelected={(showOps) => setShowBy(showOps === 'week' ? 'weekTotals' : 'seasonTotals')}
            selectedItem={showBy === 'weekTotals' ? 'week' : 'season'}
            itemSize={ComponentSize.Small}
          />
          <TitledButtonGroup
            title="Show by"
            useUpperCase
            buttonItems={['total', 'average']}
            onItemSelected={(showOps) => setTotalOrAverageShowBy(showOps as ShowByTotalOrAverage)}
            selectedItem={totalOrAverageShowBy}
            itemSize={ComponentSize.Small}
          />
        </div>
      )} */}
      {selectedSubCategory === 'Passing' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['yards', 'completions', 'attempts', 'percentage', 'yards per pass']}
          onItemSelected={(opt) => setPassSortBy(convertLabelCodeText(opt) as PassingSortBy)}
          selectedItem={convertLabelCodeText(passSortBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
      {selectedSubCategory === 'Rushing' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['yards', 'attempts', 'yards per carry']}
          onItemSelected={(opt) => setRushSortBy(convertLabelCodeText(opt) as RushingSortBy)}
          selectedItem={convertLabelCodeText(rushSortBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
      {selectedSubCategory === 'YardsAllowed' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['total', 'pass', 'rush']}
          onItemSelected={(opt) => setSortYardsAllowedBy(convertYardsAllowedLabel(opt) as YardsAllowedSortBy)}
          selectedItem={convertYardsAllowedLabel(sortYardsAllowedBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
      {selectedSubCategory === 'Total' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['drives', 'yards', 'plays', 'yards per play', 'time of possession']}
          onItemSelected={(opt) => setSortTotalsBy(convertLabelCodeText(opt) as TotalsSortBy)}
          selectedItem={convertLabelCodeText(sortTotalsBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
      {selectedSubCategory === 'ScoringBreakdown' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={[
            'points scored',
            'total tds',
            'pass td',
            'rush td',
            'fg',
            'pat',
            '2pt-pat',
            'safeties',
            'dst td',
          ]}
          onItemSelected={(opt) => setSortScoringBreakdownBy(convertLabelCodeText(opt) as ScoringBreakdownSortBy)}
          selectedItem={convertLabelCodeText(sortScoringBreakdownBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
      {selectedSubCategory === 'ScoringAgainstBreakdown' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['points allowed', 'total tds', 'pass td', 'rush td', 'fg', 'pat', '2pt-pat']}
          onItemSelected={(opt) =>
            setSortScoringAgainstBreakdownBy(convertLabelCodeText(opt) as ScoringAgainstBreakdownSortBy)
          }
          selectedItem={convertLabelCodeText(sortScoringAgainstBreakdownBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'FirstDownEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['total 1st downs', '1st downs from penalties', '1st downs by pass', '1st downs by rush']}
          onItemSelected={(opt) => setSortFirstDownEfficiencyBy(convertLabelCodeText(opt) as FirstDownEfficiencySortBy)}
          selectedItem={convertLabelCodeText(sortFirstDownEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'ThirdDownEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['3rd down attempts', '3rd down converted', '3rd down efficiency']}
          onItemSelected={(opt) => setThirdDownEfficiencyBy(convertLabelCodeText(opt) as ThirdDownEfficiencySortBy)}
          selectedItem={convertLabelCodeText(sortThirdDownEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'FourthDownEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['4th down attempts', '4th down converted', '4th down efficiency']}
          onItemSelected={(opt) => setFourthDownEfficiencyBy(convertLabelCodeText(opt) as FourthDownEfficiencySortBy)}
          selectedItem={convertLabelCodeText(sortFourthDownEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'RedZoneEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['red zone attempts', 'red zone scored', 'red zone efficiency']}
          onItemSelected={(opt) => setRedZoneEfficiencyBy(convertLabelCodeText(opt) as RedZoneEfficiencySortBy)}
          selectedItem={convertLabelCodeText(sortRedZoneEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'PassIncompletePercentage' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['passing attempt against', 'pass incompletions', 'pass incomplete percentage']}
          onItemSelected={(opt) => setSortPassingDefenseSubCatBy(convertLabelCodeText(opt) as PassingDefenseSortBy)}
          selectedItem={convertLabelCodeText(sortPassingDefenseSubCatBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'ThirdDownStopEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['3rd down attempts against', '3rd down stops', '3rd down stop efficiency']}
          onItemSelected={(opt) =>
            setSortThirdDownDefenseEfficiencyBy(convertLabelCodeText(opt) as ThirdDownDefenseEfficiencySortBy)
          }
          selectedItem={convertLabelCodeText(sortThirdDownDefenseEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'FourthDownStopEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['4th down attempts against', '4th down stops', '4th down stop efficiency']}
          onItemSelected={(opt) =>
            setSortFourthDownDefenseEfficiencyBy(convertLabelCodeText(opt) as FourthDownDefenseEfficiencySortBy)
          }
          selectedItem={convertLabelCodeText(sortFourthDownDefenseEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'RedZoneStopEfficiency' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['red zone attempts against', 'red zone stops', 'red zone stop efficiency']}
          onItemSelected={(opt) =>
            setSortRedZoneDefenseEfficiencyBy(convertLabelCodeText(opt) as RedZoneDefenseEfficiencySortBy)
          }
          selectedItem={convertLabelCodeText(sortRedZoneDefenseEfficiencyBy)}
          itemSize={ComponentSize.Medium}
        />
      )}

      {selectedSubCategory === 'SackedTimes' && (
        <TitledButtonGroup
          title="Sort by"
          useUpperCase
          buttonItems={['times sacked', 'yards lost']}
          onItemSelected={(opt) => setSortSackedTimesBy(convertLabelCodeText(opt) as SackedTimesSortBy)}
          selectedItem={convertLabelCodeText(sortSackedTimesBy)}
          itemSize={ComponentSize.Medium}
        />
      )}
    </div>
  );
};

export default ShowByFilters;
